import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, BedDouble, Bath, Ruler, Home, Eye, Check, Phone } from "lucide-react";
import { fetchProperties, formatPrice } from "../../lib/api";
import Reveal from "./Reveal";
import WhatsAppIcon from "./WhatsAppIcon";
import { OFFICE, typeLabel, propertyWhatsApp } from "../../data/content";

const FILTERS = [
  { value: "todas", label: "Todas" },
  { value: "venta", label: "En venta" },
  { value: "renta", label: "En renta" },
];

export const PropertyCard = ({ property }) => {
  const navigate = useNavigate();
  const isUnavailable = property.status !== "disponible";
  const goDetail = () => { if (!isUnavailable) navigate(`/propiedad/${property.id}`); };

  return (
    <div
      className={`property-card${isUnavailable ? " is-unavailable" : ""}`}
      role={isUnavailable ? "article" : "link"}
      tabIndex={isUnavailable ? -1 : 0}
      onClick={goDetail}
      onKeyDown={(e) => !isUnavailable && (e.key === "Enter" || e.key === " ") && goDetail()}
      data-testid={`property-card-${property.id}`}
    >
      <div className="property-media">
        {property.cover_photo ? (
          <img src={property.cover_photo} alt={property.title} loading="lazy" />
        ) : (
          <div className="placeholder-icon">
            <Home size={54} strokeWidth={1.2} />
          </div>
        )}
        <span className={`prop-badge ${property.operation === "renta" ? "renta" : ""}`}>
          {property.operation === "renta" ? "Renta" : "Venta"}
        </span>
        {isUnavailable && (
          <div className={`prop-status-overlay ${property.status}`}>
            <span>{property.status === "vendido" ? "Vendida" : "Rentada"}</span>
          </div>
        )}
      </div>
      <div className="property-body">
        <div className="property-price" data-testid="property-price">
          {formatPrice(property.price, property.currency)}
          {property.operation === "renta" && Number(property.price) > 0 ? (
            <span style={{ fontSize: 13, fontWeight: 600, color: "var(--rr-ink-soft)" }}> /mes</span>
          ) : null}
        </div>
        <div className="property-title">{property.title}</div>
        {property.location ? (
          <div className="property-location">
            <MapPin size={14} /> {property.location}
          </div>
        ) : null}
        <div className="property-specs">
          <span>{typeLabel(property.property_type)}</span>
          {Number(property.bedrooms) > 0 && (
            <span><BedDouble size={14} /> {property.bedrooms}</span>
          )}
          {Number(property.bathrooms) > 0 && (
            <span><Bath size={14} /> {property.bathrooms}</span>
          )}
          {Number(property.area_m2) > 0 && (
            <span><Ruler size={14} /> {property.area_m2} m²</span>
          )}
        </div>
        {/* Show first 3 features as quick bullets */}
        {property.features && property.features.length > 0 && (
          <ul className="prop-features-preview" data-testid={`property-features-${property.id}`}>
            {property.features.slice(0, 3).map((f, i) => (
              <li key={i}><Check size={11} strokeWidth={2.5} /> {f}</li>
            ))}
            {property.features.length > 3 && (
              <li className="more-features">+{property.features.length - 3} más…</li>
            )}
          </ul>
        )}
        <div className="property-actions">
          <a
            href={propertyWhatsApp(property.title)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-whatsapp btn-sm"
            onClick={(e) => e.stopPropagation()}
            data-testid={`property-wa-${property.id}`}
          >
            <WhatsAppIcon size={15} /> WhatsApp
          </a>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              goDetail();
            }}
            data-testid={`property-view-${property.id}`}
          >
            <Eye size={15} /> Ver detalles
          </button>
        </div>
      </div>
    </div>
  );
};

const InmobiliariaSection = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("todas");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    const params = filter === "todas" ? {} : { operation: filter };
    fetchProperties(params)
      .then((data) => {
        if (mounted) {
          setProperties(data);
          setError(false);
        }
      })
      .catch(() => mounted && setError(true))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [filter]);

  return (
    <section id="inmobiliaria" className="section-inmobiliaria" data-testid="inmobiliaria-section">
      <div className="rr-container">
        <Reveal className="section-head">
          <div>
            <span className="section-eyebrow" style={{ color: "var(--rr-royal)" }}>
              Inmobiliaria Rendón Ramos
            </span>
            <h2 className="section-title font-display">Propiedades en venta y renta</h2>
            <p className="section-desc">
              Encuentre su próxima casa, terreno o local con el respaldo legal de nuestro
              despacho jurídico. Operaciones claras y seguras.
            </p>
            {/* +100 badge */}
            <div className="sold-badge" data-testid="sold-badge">
              <span className="sold-badge-number">+100</span>
              <span className="sold-badge-text">propiedades vendidas con éxito</span>
            </div>
          </div>
          <div className="filter-group" data-testid="property-filters">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                className={`filter-btn ${filter === f.value ? "active" : ""}`}
                onClick={() => setFilter(f.value)}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        {loading ? (
          <div className="property-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-card">
                <div className="skeleton-media" />
                <div className="skeleton-line" style={{ width: "56%" }} />
                <div className="skeleton-line" style={{ width: "78%" }} />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="empty-state" data-testid="properties-error">
            <h4 className="font-display">No pudimos cargar las propiedades</h4>
            <p>Intente de nuevo más tarde o contáctenos por teléfono.</p>
            <a href={OFFICE.phoneTel} className="btn btn-call" style={{ marginTop: 18 }}>
              <Phone size={16} /> {OFFICE.phoneDisplay}
            </a>
          </div>
        ) : properties.length === 0 ? (
          <div className="empty-state" data-testid="properties-empty">
            <Home size={44} strokeWidth={1.2} style={{ color: "var(--rr-royal)", margin: "0 auto" }} />
            <h4 className="font-display">
              {filter === "renta"
                ? "Por ahora no hay propiedades en renta"
                : filter === "venta"
                ? "Por ahora no hay propiedades en venta"
                : "Muy pronto publicaremos propiedades"}
            </h4>
            <p>
              Llámenos y con gusto le informamos sobre las propiedades disponibles.
            </p>
            <a href={OFFICE.phoneTel} className="btn btn-royal" style={{ marginTop: 18 }} data-testid="empty-call-btn">
              <Phone size={16} /> Llamar: {OFFICE.phoneDisplay}
            </a>
          </div>
        ) : (
          <div className="property-grid" data-testid="property-grid">
            {properties.map((p, idx) => (
              <Reveal key={p.id} delay={idx * 0.08}>
                <PropertyCard property={p} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InmobiliariaSection;
